import { Settings } from '@streamr/tv-engine';
import { EventEmitter } from 'eventemitter3';
import { isIOS } from 'react-device-detect';
import { getIosFrameRotation } from '../../utils/getIosFrameRotation';
import { isUserFacingVideoTrack } from '../../utils/media';
import { generateThumbnail } from '../../utils/thumbnail';
import { AudioProducer } from './AudioProducer';
import { MediaPacketizer } from './MediaPacketizer';
import { VideoProducer } from './VideoProducer';
export class MediaProducer extends EventEmitter {
    videoProducer;
    audioProducer;
    mediaPacketizer;
    videoDataCounter = 0;
    audioDataCounter = 0;
    currentMediaStreamId;
    screenSharing = false;
    videoEncodingQuality = 'sd';
    lastVideoFrameTimestampMicros = 0;
    lastAudioFrameTimestampMicros = 0;
    lastAudioFrameArrivalTimeMicros = 0;
    constructor() {
        super();
        this.videoProducer = new VideoProducer();
        this.videoProducer.on('videoData', (videoData) => {
            this.onEncodedVideoData(videoData);
        });
        this.audioProducer = new AudioProducer();
        this.audioProducer.on('audioData', (audioData) => {
            this.onEncodedAudioData(audioData);
        });
        this.audioProducer.on('audioVolume', (volume) => {
            this.emit('audioVolume', volume);
        });
        this.mediaPacketizer = new MediaPacketizer();
        this.mediaPacketizer.on('mediaPacketFragment', (fragment) => {
            this.emit('mediaPacketFragment', fragment);
        });
    }
    async start(stream) {
        console.log('MediaProducer::start(), stream', stream.id, stream.getTracks());
        if (this.currentMediaStreamId !== undefined) {
            console.log('mediaStreamId changed', this.currentMediaStreamId);
        }
        this.currentMediaStreamId = stream.id;
        console.log('tracks2', stream.getTracks());
        try {
            const videoTrack = stream.getVideoTracks()[0];
            const audioTrack = stream.getAudioTracks()[0];
            if (audioTrack) {
                const settings = audioTrack.getSettings();
                console.log('Audio track sample rate:', settings.sampleRate);
                await this.audioProducer.start(audioTrack);
            }
            if (videoTrack) {
                this.mediaPacketizer.setIsVideoActive(true);
                this.screenSharing = !!videoTrack.isScreenShare;
                await this.videoProducer.start(videoTrack);
            }
            else {
                this.mediaPacketizer.setIsVideoActive(false);
            }
        }
        catch (error) {
            console.error('Failed to start MediaProducer', error);
        }
    }
    async stop() {
        console.log('MediaProducer::stop()');
        await this.videoProducer.stop();
        await this.audioProducer.stop();
        this.lastVideoFrameTimestampMicros = 0;
        this.lastAudioFrameTimestampMicros = 0;
        this.lastAudioFrameArrivalTimeMicros = 0;
    }
    onEncodedVideoData(videoData) {
        const currentTimeMicros = Math.round((performance.now() + performance.timeOrigin) * 1000);
        let videoTimestampMicros = Number(videoData.timestamp);
        if (this.lastAudioFrameArrivalTimeMicros !== 0) {
            const realTimeDiff = currentTimeMicros - this.lastAudioFrameArrivalTimeMicros;
            videoTimestampMicros =
                realTimeDiff + this.lastAudioFrameTimestampMicros;
        }
        const finalTimestamp = BigInt(videoTimestampMicros);
        this.lastVideoFrameTimestampMicros = videoTimestampMicros;
        if (this.videoDataCounter % 100 === 0) {
            const timeDiff = this.lastAudioFrameTimestampMicros -
                this.lastVideoFrameTimestampMicros;
            console.log('Audio is ahead of video by ', Math.round(timeDiff / 1000), 'ms');
        }
        const videoFrame = {
            frameNumber: BigInt(this.videoDataCounter++),
            timestamp: finalTimestamp,
            duration: videoData.duration,
            isKeyFrame: videoData.isKeyFrame,
            data: videoData.data,
            extradata: videoData.extradata,
            codedWidth: videoData.codedWidth,
            codedHeight: videoData.codedHeight,
            codec: videoData.codec,
            isScreenSharing: this.screenSharing,
            rotation: this.calculateRotation(),
        };
        this.mediaPacketizer.addVideoFrame(videoFrame, this.currentMediaStreamId ?? '');
    }
    onEncodedAudioData(audioData) {
        this.audioDataCounter++;
        this.lastAudioFrameArrivalTimeMicros = Math.round((performance.now() + performance.timeOrigin) * 1000);
        const finalTimestamp = audioData.timestamp;
        this.lastAudioFrameTimestampMicros = Number(audioData.timestamp);
        const audioFrame = {
            data: audioData.data,
            timestamp: finalTimestamp,
            duration: BigInt(audioData.duration),
            frameNumber: BigInt(this.audioDataCounter),
            isKeyFrame: audioData.isKeyFrame,
            extradata: audioData.extradata,
        };
        this.mediaPacketizer.addAudioFrames([audioFrame], this.currentMediaStreamId ?? '');
    }
    setVideoEncodingQuality(videoEncodingQuality) {
        this.videoEncodingQuality = videoEncodingQuality;
        let bitrate = Settings.videoEncoderBitrateSd;
        if (videoEncodingQuality === 'hd') {
            bitrate = Settings.videoEncoderBitrateHd;
        }
        else if (videoEncodingQuality === 'uhd') {
            bitrate = Settings.videoEncoderBitrateUhd;
        }
        this.videoProducer.setVideoEncodingBitrate(bitrate);
    }
    getVideoEncodingQuality() {
        return this.videoEncodingQuality;
    }
    /**
     * Generates a thumbnail from the current video track
     * @returns A promise that resolves to a Blob containing the thumbnail image
     */
    async generateThumbnail() {
        const videoTrack = this.videoProducer.getTrack();
        return generateThumbnail(videoTrack);
    }
    /**
     * Compute the clockwise rotation (in degrees) needed to render a captured
     * camera frame upright relative to the current device orientation.
     *
     * - Returns 0 on platforms that don’t require adjustments (non-iOS).
     * - Considers natural vs current orientation and front vs rear lens.
     *
     * @returns Clockwise rotation in degrees.
     */
    calculateRotation() {
        // Non-iOS platforms don't require rotation correction.
        if (!isIOS) {
            return 0;
        }
        const isUserFacingCamera = isUserFacingVideoTrack(this.videoProducer.getTrack());
        return getIosFrameRotation(screen.orientation, isUserFacingCamera);
    }
}
